import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { Box, styled, useMediaQuery, useTheme, Fade } from "@mui/material";
import MapContainer from "components/map/MapContainer";
import { GoogleMap, MapRainforestMarker } from "types/map";
import MapGeoProtectedRegions from "components/map/MapGeoProtectedRegions";
import MapGeoProtectedCorridors from "components/map/MapGeoProtectedCorridors";
import MapGeoProtectedHumans from "components/map/MapGeoProtectedHumans";
import MapMarkerRainforest from "components/map/MapMarkerRainforest";
import MapMarkerRainforestPanel from "./MapMarkerRainforestPanel";
import MapMarkerRainforestDialog from "./MapMarkerRainforestDialog";
import MapLegendRainforestPanel from "./MapLegendRainforestPanel";
import MapRainforestFoundationPanel from "./MapRainforestFoundationPanel";

import protectedAreasData from "json/protectedAreas.json";
import { useForecoIntroAnimation } from "hooks/maps/useForecoIntroAnimation";
import { useMapForecoFetchAreas } from "hooks/maps/useMapForecoFetchAreas";
import { useMapZoomTracker } from "hooks/maps/useMapZoomTracker";

const FORECO_DATA = protectedAreasData[0];
const FORECO_DEFAULT_CENTER = {
  lng: FORECO_DATA.rainforestMarkers[0].lng,
  lat: FORECO_DATA.rainforestMarkers[0].lat - 1.5,
};

const START_CENTER = {
  lng: -20, // Centered more over the Atlantic as seen in screenshot
  lat: 15,
};
const DEFAULT_ZOOM_LEVEL = 2;
const MAP_TYPE_CONTROL_POSITION = 3; // google.maps.ControlPosition.TOP_RIGHT
const MAP_TYPE_CONTROL_STYLE = 1; // google.maps.MapTypeControlStyle.HORIZONTAL_BAR

const StyledForecoPanelBox = styled(Box)({
  position: "absolute",
  top: 0,
  left: 0,
  width: "100%",
  height: "100%",
  pointerEvents: "none",
  zIndex: 1000,
});

const StyledFoundationPanelBox = styled(Box)(({ theme }) => ({
  position: "absolute",
  top: theme.spacing(2),
  left: theme.spacing(2),
  width: "auto",
  maxWidth: "calc(100% - 32px)",
  zIndex: 1000,
  pointerEvents: "none",
  [theme.breakpoints.up("sm")]: {
    top: "4%",
    left: "50%",
    maxWidth: "90%",
    transform: "translateX(-50%)",
  },
}));

const StyledLegendPanelBox = styled(Box)({
  position: "absolute",
  bottom: 20,
  left: 20,
  zIndex: 1000,
  pointerEvents: "none",
});

const MapEmbedProtectedAreaForecoPage = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [map, setMap] = useState<GoogleMap | null>(null);

  const { protectedRegions, protectedCorridors, protectedHumans } =
    useMapForecoFetchAreas();

  const [hiddenCorridorIndices, setHiddenCorridorIndices] = useState<number[]>(
    [],
  );
  const hasInitializedCorridors = useRef(false);

  useEffect(() => {
    if (protectedCorridors.length > 0 && !hasInitializedCorridors.current) {
      const defaultHidden = protectedCorridors
        .map((c, i) => (c.defaultHidden ? i : -1))
        .filter((i) => i !== -1);
      setHiddenCorridorIndices(defaultHidden);
      hasInitializedCorridors.current = true;
    }
  }, [protectedCorridors]);
  const [highlightHumans, setHighlightHumans] = useState(false);
  const [selectedMarker, setSelectedMarker] =
    useState<MapRainforestMarker | null>(null);
  const [selectedMarkerIndex, setSelectedMarkerIndex] = useState<number | null>(
    null,
  );

  const [showUI, setShowUI] = useState(false);
  const [, setMapZoom] = useState<number>(DEFAULT_ZOOM_LEVEL);
  const trackMapZoom = useMapZoomTracker({
    onZoomChange: setMapZoom,
  });
  const handleMapReady = useCallback(
    (nextMap: GoogleMap) => {
      setMap(nextMap);
      trackMapZoom(nextMap);
    },
    [trackMapZoom],
  );
  const handleMapInitialized = useForecoIntroAnimation({
    startCenter: START_CENTER,
    endCenter: FORECO_DEFAULT_CENTER,
    endZoom: FORECO_DATA.defaultZoom - 2.4,
    onMapInitialized: handleMapReady,
    onZoomChange: setMapZoom,
    onIntroComplete: () => setShowUI(true),
  });

  const handleClose = useCallback(() => {
    setSelectedMarker(null);
    setSelectedMarkerIndex(null);
  }, []);

  const handleMarkerSelect = useCallback(
    (marker: MapRainforestMarker | null, index: number) => {
      setSelectedMarker(marker);
      setSelectedMarkerIndex(marker ? index : null);
    },
    [],
  );

  const handleToggleCorridor = useCallback((index: number) => {
    setHiddenCorridorIndices((prev) =>
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index],
    );
  }, []);

  const handleToggleHumans = useCallback(() => {
    setHighlightHumans((prev) => !prev);
  }, []);

  const visibleCorridors = useMemo(
    () =>
      protectedCorridors.filter(
        (_, index) => !hiddenCorridorIndices.includes(index),
      ),
    [protectedCorridors, hiddenCorridorIndices],
  );

  return (
    <MapContainer
      map={map}
      boundaryWidth="100vw"
      boundaryHeight="100vh"
      onMapInitialized={handleMapInitialized}
      center={START_CENTER}
      zoom={DEFAULT_ZOOM_LEVEL}
      mapTypeId="satellite"
      mapTypeControl={true}
      mapTypeControlOptions={{
        position: MAP_TYPE_CONTROL_POSITION,
        style: MAP_TYPE_CONTROL_STYLE,
      }}
      streetViewControl={false}
      fullscreenControl={false}
      zoomControl={false}
    >
      <>
        <StyledFoundationPanelBox>
          <MapRainforestFoundationPanel textVisible={!showUI} />
        </StyledFoundationPanelBox>

        <StyledForecoPanelBox>
          {selectedMarker && !isMobile && (
            <MapMarkerRainforestPanel
              open={!!selectedMarker}
              onClose={handleClose}
              marker={selectedMarker}
              squareFootage={
                selectedMarkerIndex !== null
                  ? protectedRegions[selectedMarkerIndex]?.squareFootage
                  : undefined
              }
            />
          )}
        </StyledForecoPanelBox>

        <Fade in={showUI} timeout={600}>
          <StyledLegendPanelBox>
            <MapLegendRainforestPanel
              protectedRegions={protectedRegions}
              protectedCorridors={protectedCorridors}
              protectedHumans={protectedHumans}
              hiddenCorridorIndices={hiddenCorridorIndices}
              highlightHumans={highlightHumans}
              onToggleCorridor={handleToggleCorridor}
              onToggleHumans={handleToggleHumans}
            />
          </StyledLegendPanelBox>
        </Fade>

        {selectedMarker && isMobile && (
          <MapMarkerRainforestDialog
            open={!!selectedMarker}
            onClose={handleClose}
            marker={selectedMarker}
            squareFootage={
              selectedMarkerIndex !== null
                ? protectedRegions[selectedMarkerIndex]?.squareFootage
                : undefined
            }
          />
        )}

        {visibleCorridors.length > 0 && (
          <MapGeoProtectedCorridors areas={visibleCorridors} map={map} />
        )}

        {protectedRegions.length > 0 && (
          <MapGeoProtectedRegions
            areas={protectedRegions}
            map={map}
            selectedAreaIndex={selectedMarkerIndex}
          />
        )}

        {protectedHumans.length > 0 && (
          <MapGeoProtectedHumans
            areas={protectedHumans}
            map={map}
            highlight={highlightHumans}
            onToggleHumans={handleToggleHumans}
          />
        )}

        {FORECO_DATA.rainforestMarkers.map(
          (marker: MapRainforestMarker, index: number) => (
            <MapMarkerRainforest
              key={index}
              map={map}
              marker={marker}
              onMarkerSelect={(m) => handleMarkerSelect(m, index)}
              isSelected={selectedMarker?.name === marker.name}
              color={protectedRegions[index]?.areaStrokeColor}
            />
          ),
        )}
      </>
    </MapContainer>
  );
};

export default MapEmbedProtectedAreaForecoPage;
